import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMessaging } from '../../contexts/MessagingContext';
import supabase from '../../utils/supabaseClient';
import MessageThread from './MessageThread';

const MessageInbox = () => {
  const { user } = useAuth();
  const { unreadCount, markAsRead } = useMessaging();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showThread, setShowThread] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    const fetchMessages = async () => {
      try {
        setLoading(true);
        
        // Get all messages for the current user (sent or received)
        const { data, error: messagesError } = await supabase
          .from('messages_with_users')
          .select('*')
          .or(`receiver_id.eq.${user.id},sender_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
        
        if (messagesError) throw messagesError;
        
        // Group messages by conversation
        const conversationMap = new Map();
        data.forEach(message => {
          // Determine if this is a sent or received message
          const isOutgoing = message.sender_id === user.id;
          const conversationPartnerId = isOutgoing ? message.receiver_id : message.sender_id;
          
          // Get partner name and ensure no email is shown
          let partnerName = isOutgoing ? message.receiver_name : message.sender_name;
          if (!partnerName || partnerName.includes('@')) {
            partnerName = 'Anonymous';
          }
          
          const partnerPhoto = isOutgoing ? message.receiver_photo : message.sender_photo;
          
          // Create a key for the conversation
          const conversationKey = `conversation_${Math.min(user.id, conversationPartnerId)}_${Math.max(user.id, conversationPartnerId)}`;
          
          if (!conversationMap.has(conversationKey)) {
            conversationMap.set(conversationKey, {
              id: conversationKey,
              partnerId: conversationPartnerId,
              partnerName: partnerName,
              partnerPhoto: partnerPhoto,
              messages: [],
              latestMessage: null,
              unreadCount: 0
            });
          }
          
          const conversation = conversationMap.get(conversationKey);
          conversation.messages.push(message);
          
          // Set the latest message
          if (!conversation.latestMessage || new Date(message.created_at) > new Date(conversation.latestMessage.created_at)) {
            conversation.latestMessage = message;
          }
          
          // Count unread messages
          if (!message.is_read && message.receiver_id === user.id) {
            conversation.unreadCount += 1;
          }
        });
        
        // Convert the map to an array and sort by latest message
        const conversationsArray = Array.from(conversationMap.values())
          .sort((a, b) => new Date(b.latestMessage.created_at) - new Date(a.latestMessage.created_at));
        
        setMessages(conversationsArray);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchMessages();
    
    // Set up a real-time subscription for new messages
    const subscription = supabase
      .channel('public:messages')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'messages',
        filter: `receiver_id=eq.${user.id}`
      }, payload => {
        fetchMessages(); // Refetch messages when a new one arrives
      })
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, [user]);
  
  // Handle selecting a message
  const handleSelectMessage = (message) => {
    // Sort the messages by created_at date
    message.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    setSelectedMessage(message);
    setShowThread(true);
    
    // Mark unread messages in this conversation as read
    if (message.unreadCount > 0) {
      // Find all unread messages from this conversation partner
      markConversationAsRead(message.partnerId);
    }
  };
  
  // Mark all messages in a conversation as read
  const markConversationAsRead = async (partnerId) => {
    try {
      // Find all unread message IDs from this conversation
      const unreadMessageIds = [];
      messages.forEach(conversation => {
        if (conversation.partnerId === partnerId) {
          conversation.messages.forEach(message => {
            if (!message.is_read && message.receiver_id === user.id) {
              unreadMessageIds.push(message.id);
            }
          });
        }
      });
      
      if (unreadMessageIds.length === 0) return;
      
      // Use the markAsRead function from MessagingContext
      await markAsRead(unreadMessageIds);
      
      // Update the local state to reflect read status
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.partnerId === partnerId) {
            return { ...msg, unreadCount: 0 };
          }
          return msg;
        });
      });
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };
  
  // Close the thread view
  const handleCloseThread = () => {
    setShowThread(false);
    setSelectedMessage(null);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p>{error}</p>
      </div>
    );
  }
  
  if (showThread && selectedMessage) {
    return (
      <MessageThread 
        conversation={selectedMessage} 
        currentUser={user} 
        onBack={handleCloseThread} 
      />
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Messages {unreadCount > 0 && <span className="ml-2 bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">{unreadCount} unread</span>}</h2>
      </div>
      
      {messages.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages</h3>
          <p className="mt-1 text-sm text-gray-500">You don't have any messages yet.</p>
        </div>
      ) : (
        <div className="space-y-1 overflow-hidden">
          {messages.map((message) => (
            <div 
              key={message.id}
              onClick={() => handleSelectMessage(message)}
              className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors duration-150 ${
                message.unreadCount > 0 
                  ? 'bg-primary-50 hover:bg-primary-100' 
                  : 'bg-white hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex-shrink-0">
                {message.partnerPhoto ? (
                  <img 
                    src={message.partnerPhoto} 
                    alt={message.partnerName} 
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 font-semibold">
                      {message.partnerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className={`text-sm font-medium ${message.unreadCount > 0 ? 'text-primary-900' : 'text-gray-900'}`}>
                    {message.partnerName}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {new Date(message.latestMessage.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className={`text-sm truncate ${message.unreadCount > 0 ? 'text-primary-700 font-medium' : 'text-gray-500'}`}>
                  {message.latestMessage.sender_id === user.id ? 'You: ' : ''}
                  {message.latestMessage.subject}
                </p>
              </div>
              {message.unreadCount > 0 && (
                <div className="ml-2 flex-shrink-0">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-600 text-white text-xs">
                    {message.unreadCount}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageInbox; 